import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Terminal, RotateCcw, Database, Users, Bot, Wallet, ListChecks, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → Development Tools tab.
 *
 * Permanent surface, but entirely hidden unless the backend is running with
 * DEV_MODE=true. In production (DEV_MODE=false) the destructive endpoints
 * return 404, and this UI renders a "Not available in production" notice.
 *
 * Every action requires typing the exact phrase "DELETE DEMO DATA" and a
 * server-side allowlist ensures real admin accounts + listings are never
 * deleted.
 */
const SCOPES = [
  { key: "all",       label: "Remove ALL demo/test data", icon: Database, desc: "Full wipe — listings + users + deals + AI + wallets + notifications." },
  { key: "listings",  label: "Remove demo/test listings", icon: ListChecks, desc: "Deletes every note not owned by a real admin, plus listing_admin_audit + featured_listings." },
  { key: "users",     label: "Remove demo/test users",    icon: Users, desc: "Deletes test users + all sessions and auth tokens. Real admins are preserved." },
  { key: "deals",     label: "Remove demo/test deals",    icon: ListChecks, desc: "Deletes deals, deal_audit, offers, escrow, payments, documents, messages." },
  { key: "ai",        label: "Remove demo/test AI data",  icon: Bot, desc: "Deletes ai_messages, ai_deal_wallets, ai_wallet_ledger, ai_usage_ledger, ai_audit, ai_public_records." },
  { key: "wallets",   label: "Remove demo/test wallets",  icon: Wallet, desc: "Deletes wallets + wallet_transactions." },
  { key: "reset_stats", label: "Reset marketplace statistics", icon: RotateCcw, desc: "Marketplace stats are live-computed from the notes collection today — this is a placeholder for future counter caches." },
];

export default function DevToolsTab() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // {scope}
  const [confirmText, setConfirmText] = useState("");
  const [forceReview, setForceReview] = useState(false);
  const [report, setReport] = useState(null);
  const [reseedOpen, setReseedOpen] = useState(false);
  const [reseedConfirm, setReseedConfirm] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/dev/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ enabled: false, error: e?.response?.data?.detail || "Unavailable" });
    }
  }, []);

  const loadPreview = useCallback(async () => {
    try {
      const r = await api.get("/admin/dev/preview");
      setPreview(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setPreview(null);
      else toast.error(e?.response?.data?.detail || "Failed to load preview");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadStatus();
      setLoading(false);
    })();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.enabled) loadPreview();
  }, [status?.enabled, loadPreview]);

  const startCleanup = (scope) => {
    setPending({ scope });
    setConfirmText("");
    setForceReview(false);
    setReport(null);
  };

  const runCleanup = async () => {
    if (!pending) return;
    if (confirmText !== "DELETE DEMO DATA") {
      toast.error('You must type exactly "DELETE DEMO DATA"');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/dev/cleanup", {
        scope: pending.scope,
        confirm: confirmText,
        force: forceReview,
      });
      setReport(r.data);
      toast.success(`Cleanup complete — ${r.data.deleted_total} records removed`);
      setPending(null);
      setConfirmText("");
      setForceReview(false);
      loadPreview();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "review_required") {
        toast.error("Ambiguous users detected — enable Force delete below");
        setReport({ ok: false, needs_review: d.needs_review, message: d.message });
      } else {
        toast.error(d || "Cleanup failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const runReseed = async () => {
    if (reseedConfirm !== "RESEED DEMO DATA") {
      toast.error('You must type exactly "RESEED DEMO DATA"');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/dev/reseed", { confirm: reseedConfirm });
      toast.success(`Reseeded ${r.data.seeded} demo notes`);
      setReseedOpen(false);
      setReseedConfirm("");
      loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reseed failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground" data-testid="devtools-loading">Loading Development Tools…</div>;

  // ---------- Production / DEV_MODE=false ----------
  if (!status?.enabled) {
    return (
      <div className="border border-border p-8 max-w-3xl" data-testid="devtools-unavailable">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <h2 className="font-display text-2xl">Development Tools disabled</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Development Tools are permanently disabled in this environment because <code className="font-mono">DEV_MODE={String(status?.dev_mode_env || "false")}</code>.
        </p>
        <p className="text-sm text-muted-foreground">
          To enable in a non-production environment, set <code className="font-mono">DEV_MODE=true</code> in the backend <code className="font-mono">.env</code> and restart the backend service.
          <br />
          In production this must remain <code className="font-mono">false</code> — the destructive endpoints do not exist at runtime when disabled.
        </p>
      </div>
    );
  }

  // ---------- DEV_MODE=true ----------
  return (
    <div className="space-y-6" data-testid="devtools-tab">
      {/* Header */}
      <div className="border border-amber-500/40 bg-amber-500/5 p-4">
        <div className="flex items-start gap-3">
          <Terminal className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-overline text-amber-700 dark:text-amber-400">DEVELOPMENT TOOLS — DESTRUCTIVE</div>
            <div className="text-sm mt-1">
              These actions permanently delete data. Real admin accounts (
              <code className="font-mono text-xs">{(status.keep_emails || []).join(", ")}</code>
              ) and real listings owned by those accounts are always preserved.
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-none" onClick={loadPreview} data-testid="devtools-refresh">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Preview / dry-run */}
      {preview && <PreviewPanel preview={preview} />}

      {/* Scope actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {SCOPES.map((s) => (
          <ScopeCard key={s.key} scope={s} onRun={() => startCleanup(s.key)} preview={preview} />
        ))}
        {/* Reseed */}
        <div className="border border-border p-4 flex flex-col" data-testid="scope-card-reseed">
          <div className="flex items-center gap-2 mb-2">
            <Play className="w-4 h-4 text-primary" />
            <div className="font-display text-lg">Reseed demo data</div>
          </div>
          <div className="text-xs text-muted-foreground flex-1 mb-3">
            Adds the 25 canonical demo listings back into the notes collection. Refuses to run unless notes is empty.
          </div>
          <Button
            variant="outline"
            className="rounded-none uppercase text-xs tracking-wider justify-self-start"
            data-testid="btn-open-reseed"
            onClick={() => { setReseedConfirm(""); setReseedOpen(true); }}
          >
            Reseed 25 demo notes
          </Button>
        </div>
      </div>

      {/* Last report */}
      {report && <LastReport report={report} />}

      {/* Confirm dialog */}
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setConfirmText(""); } }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="devtools-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Confirm destructive action</DialogTitle>
            <DialogDescription>
              You are about to run the <b>{pending?.scope}</b> cleanup. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {pending && preview && <ScopePreviewSummary scope={pending.scope} preview={preview} />}
            {pending && (pending.scope === "users" || pending.scope === "all") && preview?.users?.needs_review?.length > 0 && (
              <div className="border border-amber-500/40 bg-amber-500/5 p-3" data-testid="force-review-panel">
                <div className="font-overline text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" />
                  {preview.users.needs_review.length} ambiguous user(s) — Force delete required
                </div>
                <ul className="text-xs font-mono space-y-0.5 max-h-40 overflow-auto mb-3 pl-1">
                  {preview.users.needs_review.map((u) => (
                    <li key={u.user_id} data-testid={`force-review-${u.user_id}`}>
                      {u.email} · {u.role}
                    </li>
                  ))}
                </ul>
                <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="force-review-toggle-label">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={forceReview}
                    onChange={(e) => setForceReview(e.target.checked)}
                    data-testid="force-review-checkbox"
                  />
                  <span>
                    I have reviewed the accounts above and confirm they are all test/demo users. <b>Force delete them all</b> (their sessions, dealer profiles, applications, notifications, and every other transactional record will be cascade-cleaned).
                  </span>
                </label>
              </div>
            )}
            <div>
              <Label className="text-xs font-overline">Type <b>DELETE DEMO DATA</b> to confirm</Label>
              <Input
                className="rounded-none font-mono mt-1"
                placeholder="DELETE DEMO DATA"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="devtools-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setPending(null); setConfirmText(""); setForceReview(false); }} data-testid="btn-cancel-cleanup">Cancel</Button>
            <Button
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={runCleanup}
              disabled={
                busy ||
                confirmText !== "DELETE DEMO DATA" ||
                ((pending?.scope === "users" || pending?.scope === "all") &&
                  preview?.users?.needs_review?.length > 0 && !forceReview)
              }
              data-testid="btn-run-cleanup"
            >
              {busy ? "Running…" : "Run cleanup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reseed dialog */}
      <Dialog open={reseedOpen} onOpenChange={(o) => { if (!o) { setReseedOpen(false); setReseedConfirm(""); } }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="reseed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reseed demo data</DialogTitle>
            <DialogDescription>
              Insert the 25 canonical demo listings. Only runs if notes collection is empty.
            </DialogDescription>
          </DialogHeader>
          <Label className="text-xs font-overline">Type <b>RESEED DEMO DATA</b> to confirm</Label>
          <Input
            className="rounded-none font-mono"
            placeholder="RESEED DEMO DATA"
            value={reseedConfirm}
            onChange={(e) => setReseedConfirm(e.target.value)}
            data-testid="reseed-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setReseedOpen(false)}>Cancel</Button>
            <Button
              className="rounded-none"
              onClick={runReseed}
              disabled={busy || reseedConfirm !== "RESEED DEMO DATA"}
              data-testid="btn-run-reseed"
            >
              {busy ? "Running…" : "Reseed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Sub-components ----------
function ScopeCard({ scope, onRun, preview }) {
  const Icon = scope.icon || Database;
  const count = countForScope(scope.key, preview);
  return (
    <div className="border border-border p-4 flex flex-col" data-testid={`scope-card-${scope.key}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-primary" />
        <div className="font-display text-lg">{scope.label}</div>
      </div>
      <div className="text-xs text-muted-foreground flex-1 mb-3">{scope.desc}</div>
      <div className="flex items-center justify-between mt-auto">
        <span className="text-xs text-muted-foreground" data-testid={`scope-count-${scope.key}`}>
          {count === null ? "—" : `${count} record${count === 1 ? "" : "s"} affected`}
        </span>
        <Button
          variant="outline"
          className="rounded-none uppercase text-xs tracking-wider"
          onClick={onRun}
          data-testid={`btn-scope-${scope.key}`}
          disabled={scope.key !== "reset_stats" && count === 0}
        >
          Run
        </Button>
      </div>
    </div>
  );
}

function countForScope(scope, p) {
  if (!p) return null;
  switch (scope) {
    case "listings": return p.listings?.will_delete ?? 0;
    case "users":    return (p.users?.will_delete ?? 0) + (p.users?.needs_review?.length ?? 0);
    case "deals":    return p.deals?.total ?? 0;
    case "ai":       return p.ai?.total ?? 0;
    case "wallets":  return p.wallets?.total ?? 0;
    case "reset_stats": return null;
    case "all":      return (p.listings?.will_delete || 0) + (p.users?.will_delete || 0) + (p.users?.needs_review?.length || 0) + (p.deals?.total || 0) + (p.ai?.total || 0) + (p.wallets?.total || 0) + (p.misc?.total || 0) + (p.sessions_will_delete || 0);
    default: return null;
  }
}

function PreviewPanel({ preview }) {
  const p = preview;
  return (
    <div className="border border-border" data-testid="devtools-preview">
      <div className="border-b border-border px-4 py-3 flex items-center gap-2">
        <ListChecks className="w-4 h-4 text-primary" />
        <div className="font-overline">Preview — dry run</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
        <StatCell label="Listings" value={p.listings?.total} sub={`${p.listings?.will_delete || 0} to delete`} testid="stat-listings" />
        <StatCell label="Users" value={p.users?.total} sub={`${p.users?.will_delete || 0} to delete · ${p.users?.keep || 0} keep`} testid="stat-users" />
        <StatCell label="Deals" value={p.deals?.total} sub={`${p.deals?.collections?.deals || 0} deals`} testid="stat-deals" />
        <StatCell label="AI" value={p.ai?.total} sub={`${p.ai?.collections?.ai_messages || 0} messages`} testid="stat-ai" />
      </div>
      {(p.users?.needs_review || []).length > 0 && (
        <div className="border-t border-amber-500/40 bg-amber-500/5 p-3" data-testid="devtools-needs-review">
          <div className="font-overline text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Needs Review — {p.users.needs_review.length} user(s) require manual decision
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            Cleanup scopes that touch users will refuse to run until these users are either added to <code className="font-mono">ADMIN_EMAILS</code> (to keep) or manually deleted.
          </div>
          <ul className="text-xs font-mono space-y-1 max-h-40 overflow-auto">
            {p.users.needs_review.map((u) => (
              <li key={u.user_id} data-testid={`review-user-${u.user_id}`}>{u.email} · {u.role} · {u.created_at}</li>
            ))}
          </ul>
        </div>
      )}
      {(p.listings?.sample_will_delete || []).length > 0 && (
        <div className="border-t border-border p-3">
          <div className="font-overline text-muted-foreground mb-2">Sample listings that will be deleted</div>
          <ul className="text-xs font-mono space-y-1 max-h-40 overflow-auto">
            {p.listings.sample_will_delete.map((n) => (
              <li key={n.note_id} data-testid={`sample-listing-${n.note_id}`}>
                {n.note_id} · {n.title} · {n.status}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, sub, testid }) {
  return (
    <div className="p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-2xl">{value ?? 0}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ScopePreviewSummary({ scope, preview }) {
  const rows = [];
  const p = preview;
  const push = (l, v) => rows.push([l, v]);
  if (scope === "all" || scope === "listings") push("Notes", p.listings?.will_delete || 0);
  if (scope === "all" || scope === "users") {
    push("Users (matched test pattern)", p.users?.will_delete || 0);
    push("Users (ambiguous — force required)", p.users?.needs_review?.length || 0);
  }
  if (scope === "all" || scope === "deals") push("Deal records (all)", p.deals?.total || 0);
  if (scope === "all" || scope === "ai") push("AI records (all)", p.ai?.total || 0);
  if (scope === "all" || scope === "wallets") push("Wallet records (all)", p.wallets?.total || 0);
  if (scope === "all") push("Misc + sessions (dealers, applications, notifications…)", (p.misc?.total || 0) + (p.sessions_will_delete || 0));
  return (
    <div className="border border-border p-3 text-xs font-mono">
      <div className="font-overline text-muted-foreground mb-1">Will delete</div>
      <ul className="space-y-0.5">
        {rows.map(([l, v]) => (
          <li key={l} className="flex justify-between"><span>{l}</span><span>{v}</span></li>
        ))}
      </ul>
    </div>
  );
}

function LastReport({ report }) {
  if (!report) return null;
  return (
    <div className="border border-border p-4" data-testid="devtools-last-report">
      <div className="font-overline text-muted-foreground mb-2">Last deletion report</div>
      {report.ok === false && (
        <div className="text-sm text-amber-700 dark:text-amber-400 mb-2">{report.message}</div>
      )}
      {report.ok && (
        <>
          <div className="text-sm mb-2">
            Scope: <b>{report.scope}</b> · Total deleted: <b data-testid="report-total">{report.deleted_total}</b>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
            {Object.entries(report.deleted || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border border-border px-2 py-1" data-testid={`report-row-${k}`}>
                <span>{k}</span><span>{v}</span>
              </div>
            ))}
          </div>
          <div className="text-xs mt-3 text-muted-foreground">
            Remaining after cleanup: notes={report.remaining?.notes ?? "—"}, users={report.remaining?.users ?? "—"}, deals={report.remaining?.deals ?? "—"}, offers={report.remaining?.offers ?? "—"}
          </div>
          <div className="text-xs mt-1 text-muted-foreground">
            Kept users: <span className="font-mono">{(report.kept_users || []).join(", ")}</span>
          </div>
        </>
      )}
    </div>
  );
}
